import { HealthTopicAPIString, HealthTopic, CareProgramType } from './types';

/**
 * Health Topic Card Colors
 * These colors are hardcoded and match the UI color system for health topic cards.
 * Each color corresponds to a HealthTopicColor enum value.
 */
export const HEALTH_TOPIC_COLORS = {
  purple1: '#F2EFF5', // Cancer
  magenta3: '#FEECF2', // Cardiovascular
  turquoise3: '#CAF6F2', // Care Transitions
  secondary2: '#D2F1F9', // Diabetes & Maternal (shared)
  gray2: '#E5E5E5', // Generic
  tertiary2: '#D0FCFF', // Kidney
  green2: '#EBFBE5', // Mental & Behavioral
  lilac3: '#F8E6F3', // Pain Management & Women's (shared)
  orange3: '#FEEEE6', // Pediatric
  red3: '#FDE7E8', // Respiratory
  purple2: '#EEE8F4', // Wellbeing & Social Support
};

/**
 * Health Topic Metadata
 * Maps API health topic identifiers to UI display metadata
 */
export const HEALTH_TOPIC_METADATA: Record<
  HealthTopicAPIString,
  { healthTopic: HealthTopic; displayName: string; iconName: string }
> = {
  MENTAL_AND_BEHAVIORAL_HEALTH: {
    healthTopic: 'mentalAndBehavioral',
    displayName: 'Mental & Behavioral Health',
    iconName: 'mental-health',
  },
  WELLNESS_AND_SOCIAL_SUPPORT: {
    healthTopic: 'wellbeingAndSocialSupport',
    displayName: 'Wellness & Social Support',
    iconName: 'wellness',
  },
  CANCER_SUPPORT: {
    healthTopic: 'cancer',
    displayName: 'Cancer Support',
    iconName: 'cancer',
  },
  CARDIOVASCULAR_HEALTH: {
    healthTopic: 'cardiovascular',
    displayName: 'Cardiovascular Health',
    iconName: 'cardiovascular',
  },
  CARE_TRANSITIONS_AND_HOSPITAL_STAY: {
    healthTopic: 'careTransitions',
    displayName: 'Care Transitions & Hospital Stay',
    iconName: 'care-transitions',
  },
  DIABETES_AND_PRE_DIABETES_SUPPORT: {
    healthTopic: 'diabetes',
    displayName: 'Diabetes & Pre-Diabetes Support',
    iconName: 'diabetes',
  },
  KIDNEY_HEALTH: {
    healthTopic: 'kidney',
    displayName: 'Kidney Health',
    iconName: 'kidney',
  },
  MATERNAL_HEALTH: {
    healthTopic: 'maternal',
    displayName: 'Maternal Health',
    iconName: 'maternal',
  },
  PAIN_MANAGEMENT: {
    healthTopic: 'painManagement',
    displayName: 'Pain Management',
    iconName: 'pain-management',
  },
  PEDIATRIC_HEALTH: {
    healthTopic: 'pediatric',
    displayName: 'Pediatric Health',
    iconName: 'pediatric',
  },
  RESPIRATORY_HEALTH: {
    healthTopic: 'respiratory',
    displayName: 'Respiratory Health',
    iconName: 'respiratory',
  },
  WOMENS_HEALTH: {
    healthTopic: 'womens',
    displayName: "Women's Health",
    iconName: 'womens-health',
  },
};

/**
 * Program Section Definitions by API program type.
 * Translation keys are resolved via the app locale layer.
 */
export const PROGRAM_SECTIONS: Record<
  CareProgramType,
  {
    titleKey: string;
    descriptionKey: string;
  }
> = {
  'Health Tracker': {
    titleKey: 'tracking_programs_list_title',
    descriptionKey: 'tracking_programs_list_description',
  },
  'Educational Program': {
    titleKey: 'educational_programs_list_title',
    descriptionKey: 'educational_programs_list_description',
  },
};

/**
 * Priority order for recommended/featured care programs.
 * Programs are displayed in this order, filtering out unavailable ones.
 * Names must match the `member_facing_name` field from the API exactly (case-sensitive).
 * If programs are not available for a hospital, the list will backfill with other available options.
 */
export const RECOMMENDED_CARE_PROGRAM_NAMES = [
  'Your Health Across Your Life',
  'Making Habits Stick',
  'Managing Stress',
  'Physical Activity',
  'Anxiety Support',
  'Weight Loss Support',
  'Weekly weight',
  'Behavioral Health and Wellness',
  'Depression Support',
  'Hypertension Management',
  'Weekly Blood Pressure Tracker',
  'Diabetes Management',
  'Smoking Cessation',
  'Prediabetes Support',
  'Chronic Back Pain Support',
  'Migraine support',
];
/** A care program welcome sheet is only shown if its subscription started within this many days. */
export const WELCOME_SHEET_BOUNDARY_WINDOW_DAYS = 7;
